import { FormEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import FileInputComponent, { FileInputHandle } from 'components/shared/fileInput/FileInputComponent';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input, { InputStyles } from 'components/shared/form/Input';
import Upload from 'hooks/useDirectUpload/Upload';
import { ManualBenefitsPayer } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall } from 'styles/typography';

import BenefitFileRow from './BenefitFileRow';
import { ManualBenefitsAttachment, ManualBenefitsRecord } from './eligibilityData';
import PayerTypeahead from './PayerTypeahead';

export const MANUAL_BENEFITS_FORM_ID = 'manual-benefits-form';

// The same list the API accepts, so a file it would refuse can't be picked.
const ALLOWED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'image/tiff': ['.tif', '.tiff'],
};
// The API's limits: per file, and per record — counting the files an edit keeps.
const MAX_FILES = 10;
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export interface ManualBenefitsSubmission {
  payer: ManualBenefitsPayer;
  memberId: string;
  note: string;
  attachments: (Upload | { id: string })[];
}

interface Props {
  // The record being edited; absent when documenting for the first time.
  record: ManualBenefitsRecord | null;
  // The coverage's own details, used to start a first record.
  payerName?: string;
  memberId?: string;
  disabled?: boolean;
  onSubmit: (submission: ManualBenefitsSubmission) => void;
  // Whether the footer's Save can be pressed — the footer sits outside this form.
  onCanSaveChange: (canSave: boolean) => void;
}

export default function ManualBenefitsForm({
  record,
  payerName,
  memberId,
  disabled,
  onSubmit,
  onCanSaveChange,
}: Props) {
  const [payer, setPayer] = useState<ManualBenefitsPayer | null>(record?.payer ?? null);
  const [member, setMember] = useState(record ? (record.memberId ?? '') : (memberId ?? ''));
  const [note, setNote] = useState(record?.note ?? '');
  const [kept, setKept] = useState<ManualBenefitsAttachment[]>(record?.attachments ?? []);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<FileInputHandle>(null);

  const failed = uploads.some((upload) => upload.hasError);
  const hasFiles = kept.length + uploads.length > 0;
  const canSave = Boolean(payer?.name.trim()) && hasFiles && !uploading && !failed && !disabled;

  useEffect(() => onCanSaveChange(canSave), [canSave, onCanSaveChange]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSave || !payer) return;

    onSubmit({
      payer,
      memberId: member.trim(),
      note: note.trim(),
      attachments: [...kept.map(({ id }) => ({ id })), ...uploads],
    });
  };

  return (
    <Form
      id={MANUAL_BENEFITS_FORM_ID}
      onSubmit={submit}
      // Enter in a text input would submit the whole record from the payer or member ID field.
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') event.preventDefault();
      }}
      data-testid='manual-benefits-form'>
      <div>
        <Title>Add Benefits Manually</Title>
        <BodySmall>
          Provide the benefit information below – any manually entered information will overwrite extracted details (if
          applicable)
        </BodySmall>
      </div>

      <Field $width='500px'>
        <FieldLabel name='manual-benefits-payer'>Payer Name</FieldLabel>
        <PayerTypeahead value={payer} onChange={setPayer} initialQuery={payerName} disabled={disabled} />
      </Field>

      <Field $width='250px'>
        <FieldLabel name='manual-benefits-member-id'>Member ID (optional)</FieldLabel>
        <Input
          id='manual-benefits-member-id'
          value={member}
          onChange={(event) => setMember(event.target.value)}
          placeholder=''
          disabled={disabled}
          data-testid='manual-benefits-member-id'
        />
      </Field>

      <Field $width='500px'>
        <FieldLabel>Attach any related benefit documents</FieldLabel>
        <FileInputComponent
          ref={fileInputRef}
          allowedTypes={ALLOWED_TYPES}
          onChange={setUploads}
          onUploading={setUploading}
          uploadList='none'
          maxFiles={Math.max(0, MAX_FILES - kept.length)}
          maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
          limitMessage={`A maximum of ${MAX_FILES} files can be attached`}
        />
        {failed && <Failed>A file didn’t upload. Remove it and try again.</Failed>}
        {!hasFiles && <Hint data-testid='manual-benefits-files-required'>Attach at least one document to save.</Hint>}
        {(kept.length > 0 || uploads.length > 0) && (
          <Files>
            {kept.map((file) => (
              <BenefitFileRow
                key={file.id}
                filename={file.filename}
                onRemove={disabled ? undefined : () => setKept((current) => current.filter(({ id }) => id !== file.id))}
              />
            ))}
            {uploads.map((upload) => (
              <BenefitFileRow
                key={upload.id}
                filename={upload.file.name}
                error={upload.hasError}
                progress={upload.progress}
                onRemove={disabled ? undefined : () => fileInputRef.current?.removeUpload(upload.id)}
              />
            ))}
          </Files>
        )}
      </Field>

      <Field $width='500px'>
        <FieldLabel name='manual-benefits-note'>Notes (optional)</FieldLabel>
        <TextArea
          id='manual-benefits-note'
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          disabled={disabled}
          data-testid='manual-benefits-note'
        />
      </Field>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.div`
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: ${FONT_WEIGHT_MEDIUM};
  color: ${colors.black100};
`;

const Field = styled.div<{ $width: string }>`
  max-width: ${({ $width }) => $width};
`;

const Files = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const Failed = styled(BodySmall)`
  margin-top: 8px;
  color: ${colors.red100};
`;

const Hint = styled(BodySmall)`
  margin-top: 8px;
  color: ${colors.black50};
`;

const TextArea = styled.textarea<{ $hasError?: boolean }>`
  ${InputStyles}
  resize: vertical;
  font-family: inherit;
`;
